import React, { useCallback, useRef, useState } from "react";
import { Card, CardContent, Button } from "../../";
import { cn } from "../../utils";
import { IconX, IconFile, IconUpload } from "@tabler/icons-react";

export interface DragAndDropFileUploadProps {
  onFilesSelected: (files: File[]) => void;
  acceptedFileTypes?: string[];
  maxFiles?: number;
  className?: string;
}

export function DragAndDropFileUpload({
  onFilesSelected,
  acceptedFileTypes = [],
  maxFiles = 1,
  className,
}: DragAndDropFileUploadProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const acceptedFileTypesString = acceptedFileTypes.join(",");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    (selectedFiles: FileList | null) => {
      if (!selectedFiles) return;

      const newFiles = Array.from(selectedFiles);
      const validFiles = acceptedFileTypes.length
        ? newFiles.filter(file =>
            acceptedFileTypes.some(
              type =>
                file.type.includes(type.replace("*", "")) ||
                file.name.endsWith(type.replace("*", ""))
            )
          )
        : newFiles;

      const updatedFiles =
        maxFiles > 1 ? [...files, ...validFiles].slice(0, maxFiles) : validFiles.slice(0, maxFiles);

      setFiles(updatedFiles);
      onFilesSelected(updatedFiles);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [files, maxFiles, acceptedFileTypes, onFilesSelected]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      handleFileChange(e.dataTransfer.files);
    },
    [handleFileChange]
  );

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
    onFilesSelected(newFiles);
  };

  return (
    <Card className={cn("al-w-full", className)}>
      <CardContent className="!al-p-6">
        <div
          className={cn(
            "al-relative al-border-2 al-border-dashed al-rounded-lg al-p-6 al-transition-colors",
            isDragging
              ? "al-border-primary al-bg-primary/5"
              : "al-border-muted-foreground/25 hover:al-border-primary/50",
            files.length > 0 ? "al-pb-2" : ""
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openFileDialog}
        >
          <input
            type="file"
            ref={fileInputRef}
            className="al-hidden"
            onChange={e => handleFileChange(e.target.files)}
            accept={acceptedFileTypesString}
            multiple={maxFiles > 1}
          />

          <div className="al-flex al-flex-col al-items-center al-justify-center al-gap-1 al-text-center">
            <IconUpload className="al-size-12 al-text-muted-foreground/50" />
            <p className="al-text-sm al-font-medium">
              Drag & drop {maxFiles > 1 ? "files" : "a file"} here, or click to select
            </p>
            <p className="al-text-xs al-text-muted-foreground">
              {acceptedFileTypes.length > 0
                ? `Accepts: ${acceptedFileTypes.join(", ")}`
                : "All file types supported"}
              {maxFiles > 1 ? ` (max ${maxFiles} files)` : ""}
            </p>

            <Button
              variant="outline"
              size="sm"
              className="al-mt-2"
              onClick={e => {
                e.stopPropagation();
                openFileDialog();
              }}
            >
              Select file{maxFiles > 1 ? "s" : ""}
            </Button>
          </div>

          {files.length > 0 && (
            <div className="al-mt-4 al-space-y-2">
              {files.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="al-flex al-items-center al-gap-2 al-rounded-md al-border al-border-border al-p-2 al-bg-background"
                >
                  <IconFile className="al-h-4 al-w-4 al-text-muted-foreground" />
                  <span className="al-text-sm al-truncate al-flex-1">{file.name}</span>
                  <span className="al-text-xs al-text-muted-foreground">
                    {(file.size / 1024).toFixed(0)} KB
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="al-h-6 al-w-6"
                    title="Remove file"
                    onClick={e => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                  >
                    <IconX className="al-h-4 al-w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
